import { cvLeisureActivitiesAPI } from 'api/CvAPI'
import { IItemDraftHandle } from 'types/hocTypes'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ICv, ILeisureActivity } from 'types/cvsInterfaces'
import withProfileCard from 'Components/reusable/HOC/withProfileCard'
import { getLeisureActivityInitialData } from 'Components/reusable/DataContext/InitialData'
import { leisureActivitySchema } from 'Components/reusable/DataContext/ValidationSchema'
import LeisureActivityItemCard from './LeisureActivityItemCard.tsx'
import LeisureActivityModal from './LeisureActivityModal.tsx'
import { CvId } from 'types/ids.ts'

interface ILeisureActivitiesCardProps {
  editable?: boolean
  cv?: ICv
  setUpdatedAt?: () => void
  closeItem?: () => void
  replaceOrAddItem?: (newItem: unknown) => unknown[]
  openItem?: (item: unknown) => void
  editedItem?: ILeisureActivity | null
  items?: unknown[]
  header: string
  updateProgress?: () => void
  /** Dialog drafts from the CV wizard (see `withProfileCard`'s `itemDraft`). */
  itemDraft?: IItemDraftHandle
  setLeisureActivities?: (items: ILeisureActivity[]) => void
}

const LeisureActivitiesCard: React.FC<ILeisureActivitiesCardProps> = ({
  cv,
  editable,
  items,
  setUpdatedAt,
  closeItem,
  replaceOrAddItem,
  openItem,
  editedItem,
  header,
  updateProgress,
  itemDraft,
  setLeisureActivities,
}) => {
  const { i18n } = useTranslation()
  const leisureItems = useMemo(() => (items ?? []) as ILeisureActivity[], [items])

  const itemCards = useMemo(
    () =>
      leisureItems.map((item, i) => (
        <LeisureActivityItemCard
          key={`${item.id}-${i18n.language}`}
          editable={editable}
          item={item}
          onOpen={() => openItem?.(item)}
          header={header}
          showDivider={i < leisureItems.length - 1}
        />
      )),
    [leisureItems, editable, header, openItem, i18n.language]
  )

  return (
    <>
      {itemCards}
      {editedItem !== undefined && (
        <LeisureActivityModal
          key={editedItem?.id ?? 'new'}
          item={editedItem as unknown as Record<string, unknown> | null | undefined}
          draft={itemDraft?.forItem(editedItem ?? null)}
          cvId={cv?.id ?? (0 as CvId)}
          initialData={getLeisureActivityInitialData(cv?.id ?? (0 as CvId))}
          schema={leisureActivitySchema()}
          api={
            cvLeisureActivitiesAPI as unknown as {
              create: (cvId: number | undefined, item: unknown) => Promise<ILeisureActivity>
              delete: (id: number, cvId?: number) => Promise<void>
              save?: (item: unknown, data: unknown) => Promise<ILeisureActivity>
              networkSave?: (item: unknown, data: unknown) => Promise<ILeisureActivity>
            }
          }
          localeBase="profile.leisureActivity.modal"
          maxWidth="xl"
          onClose={(value?: Record<string, unknown>) => {
            const newItem = value?.newItem as ILeisureActivity | undefined
            closeItem?.()
            if (newItem) {
              setUpdatedAt?.()
              const updatedItems = (replaceOrAddItem?.(newItem) ?? []) as ILeisureActivity[]
              setLeisureActivities?.(updatedItems)
              updateProgress?.()
            }
          }}
          header={header}
          noEscClose
        />
      )}
    </>
  )
}

export default withProfileCard(LeisureActivitiesCard)
